import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { deleteData, fetchData, postData, putData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { fetchFieldDescriptions as fetchFieldDescriptionsFromCritical } from '../../redux/slices/fieldDescriptionSlice';
import './AdminContentEditors.css';

const EMPTY_FIELD = { tableName: '', columnName: '', description: '' };

const getFieldId = (field) => field?.ID ?? field?.id;
const getTableName = (field) => String(field?.TableName ?? field?.tableName ?? '').trim();
const getColumnName = (field) => String(field?.ColumnName ?? field?.columnName ?? '').trim();
const getDescription = (field) => String(field?.Description ?? field?.description ?? '');

const humanizeKey = (value) => String(value || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[-_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const AdminFieldDesc = () => {
  const apiUrls = useSelector(selectApiUrls);
  const dispatch = useDispatch();
  const [fieldDescriptions, setFieldDescriptions] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [description, setDescription] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newField, setNewField] = useState(EMPTY_FIELD);
  const [columnOptions, setColumnOptions] = useState([]);
  const [columnLoading, setColumnLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState('');
  const [notice, setNotice] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilter, setTableFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  useGuardedRealtimeList({ apiKey: 'hoverDescriptions', apply: setFieldDescriptions,
    blocked: loading || refreshing || Boolean(pendingAction || editingField || showModal || deleteTarget) });

  const fetchFieldDescriptions = useCallback(async ({ forceRefresh = false, background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const data = await dispatch(
        fetchFieldDescriptionsFromCritical({
          forceRefresh,
          fallbackToLegacy: true,
        })
      );
      setFieldDescriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`Failed to fetch field descriptions: ${err.message || err}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchFieldDescriptions();
  }, [fetchFieldDescriptions]);

  const tableNames = useMemo(() => (
    [...new Set(fieldDescriptions.map(getTableName).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right))
  ), [fieldDescriptions]);

  const availableApiTables = useMemo(() => (
    Object.keys(apiUrls || {})
      .filter((key) => Boolean(apiUrls[key]))
      .sort((left, right) => humanizeKey(left).localeCompare(humanizeKey(right)))
  ), [apiUrls]);

  const filteredDescriptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return fieldDescriptions.filter((field) => {
      const tableName = getTableName(field);
      if (tableFilter !== 'all' && tableName !== tableFilter) return false;
      if (!normalizedSearch) return true;
      return [tableName, getColumnName(field), getDescription(field)]
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [fieldDescriptions, searchTerm, tableFilter]);

  const describedCount = useMemo(
    () => fieldDescriptions.filter((field) => getDescription(field).trim()).length,
    [fieldDescriptions]
  );

  const handleEdit = (field) => {
    setEditingField(field);
    setDescription(getDescription(field));
    setError(null);
    setNotice('');
  };

  const handleSave = async () => {
    if (!editingField || !description.trim()) {
      setError('Description cannot be empty.');
      return;
    }

    const fieldId = getFieldId(editingField);
    setPendingAction(`edit:${fieldId}`);
    setError(null);
    setNotice('');
    try {
      await dispatch(putData({
        url: apiUrls.hoverDescriptions,
        data: {
          ID: fieldId,
          TableName: getTableName(editingField),
          ColumnName: getColumnName(editingField),
          Description: description.trim(),
        },
      })).unwrap();
      setEditingField(null);
      setDescription('');
      await fetchFieldDescriptions({ forceRefresh: true, background: true });
      setNotice('Field description updated.');
    } catch (err) {
      setError(`Failed to save field: ${err.message || err}`);
    } finally {
      setPendingAction('');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const fieldId = getFieldId(deleteTarget);
    setPendingAction(`delete:${fieldId}`);
    setError(null);
    setNotice('');
    try {
      await dispatch(deleteData({
        url: apiUrls.hoverDescriptions,
        deleteId: { ID: fieldId },
      })).unwrap();
      setDeleteTarget(null);
      await fetchFieldDescriptions({ forceRefresh: true, background: true });
      setNotice('Field description deleted.');
    } catch (err) {
      setError(`Failed to delete field: ${err.message || err}`);
    } finally {
      setPendingAction('');
    }
  };

  const handleAddNew = async (event) => {
    event.preventDefault();
    if (!newField.tableName || !newField.columnName || !newField.description.trim()) {
      setModalError('Table, column, and description are required.');
      return;
    }

    setPendingAction('create');
    setModalError('');
    setNotice('');
    try {
      await dispatch(postData({
        url: apiUrls.hoverDescriptions,
        data: {
          tableName: newField.tableName,
          columnName: newField.columnName,
          description: newField.description.trim(),
        },
      })).unwrap();
      setShowModal(false);
      setNewField(EMPTY_FIELD);
      setColumnOptions([]);
      await fetchFieldDescriptions({ forceRefresh: true, background: true });
      setNotice('Field description created.');
    } catch (err) {
      setModalError(`Failed to add field: ${err.message || err}`);
    } finally {
      setPendingAction('');
    }
  };

  const handleTableChange = async (selectedTable) => {
    setNewField((current) => ({ ...current, tableName: selectedTable, columnName: '' }));
    setColumnOptions([]);
    setModalError('');
    if (!selectedTable || !apiUrls[selectedTable]) return;

    setColumnLoading(true);
    try {
      const data = await dispatch(fetchData({ url: apiUrls[selectedTable] })).unwrap();
      const firstRecord = Array.isArray(data) ? data[0] : data?.data?.[0];
      setColumnOptions(firstRecord && typeof firstRecord === 'object'
        ? Object.keys(firstRecord).sort((left, right) => left.localeCompare(right))
        : []);
      if (!firstRecord) {
        setModalError('No sample record was available to discover columns for this table.');
      }
    } catch (err) {
      setModalError(`Failed to fetch columns: ${err.message || err}`);
    } finally {
      setColumnLoading(false);
    }
  };

  const closeCreateModal = () => {
    if (pendingAction) return;
    setShowModal(false);
    setNewField(EMPTY_FIELD);
    setColumnOptions([]);
    setModalError('');
  };

  if (loading) {
    return (
      <div className="admin-editor__loading" role="status" aria-live="polite">
        <Spinner animation="border" variant="primary" />
        <div><strong>Loading field descriptions</strong><span>Preparing the content editor...</span></div>
      </div>
    );
  }

  return (
    <section className="admin-editor" aria-labelledby="field-descriptions-title">
      <header className="admin-editor__header">
        <div>
          <p className="admin-editor__eyebrow">Fields &amp; content</p>
          <h2 id="field-descriptions-title">Field descriptions</h2>
          <p>Maintain the contextual descriptions displayed alongside fields throughout the portal.</p>
        </div>
        <div className="admin-editor__header-actions">
          <Button
            variant="outline-secondary"
            onClick={() => fetchFieldDescriptions({ forceRefresh: true, background: true })}
            disabled={refreshing || Boolean(pendingAction)}
          >
            <i className={`bi bi-arrow-clockwise me-2 ${refreshing ? 'admin-editor__spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
          <Button onClick={() => setShowModal(true)} disabled={Boolean(pendingAction)}>
            <i className="bi bi-plus-lg me-2" />Add field
          </Button>
        </div>
      </header>

      <div className="admin-editor__metrics" aria-label="Field description summary">
        <div><span>Descriptions</span><strong>{fieldDescriptions.length}</strong></div>
        <div><span>Tables covered</span><strong>{tableNames.length}</strong></div>
        <div className="admin-editor__metric--success"><span>With content</span><strong>{describedCount}</strong></div>
        <div className="admin-editor__metric--muted"><span>Visible results</span><strong>{filteredDescriptions.length}</strong></div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="admin-editor__alert">
          <Alert.Heading>We couldn&apos;t complete that action</Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice('')} className="admin-editor__alert" aria-live="polite">
          <i className="bi bi-check-circle-fill me-2" />{notice}
        </Alert>
      )}

      <div className="admin-editor__workspace">
        <div className="admin-editor__toolbar">
          <div className="admin-editor__search">
            <i className="bi bi-search" aria-hidden="true" />
            <Form.Control
              type="search"
              placeholder="Search tables, columns, or descriptions"
              aria-label="Search field descriptions"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} aria-label="Clear search">
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>
          <Form.Group className="admin-editor__filter">
            <Form.Label htmlFor="field-description-table-filter">Table</Form.Label>
            <Form.Select
              id="field-description-table-filter"
              value={tableFilter}
              onChange={(event) => setTableFilter(event.target.value)}
            >
              <option value="all">All tables</option>
              {tableNames.map((tableName) => <option key={tableName} value={tableName}>{tableName}</option>)}
            </Form.Select>
          </Form.Group>
          {(searchTerm || tableFilter !== 'all') && (
            <Button variant="link" onClick={() => { setSearchTerm(''); setTableFilter('all'); }}>
              Reset filters
            </Button>
          )}
          <span className="admin-editor__result-count">
            Showing {filteredDescriptions.length} of {fieldDescriptions.length}
          </span>
        </div>

        {filteredDescriptions.length > 0 ? (
          <Table responsive hover className="admin-editor__table align-middle">
            <thead>
              <tr>
                <th>Table</th>
                <th>Column</th>
                <th>Description</th>
                <th><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filteredDescriptions.map((field) => {
                const fieldId = getFieldId(field);
                const isEditing = String(getFieldId(editingField)) === String(fieldId);
                return (
                  <tr key={fieldId}>
                    <td><Badge bg="light" text="dark">{getTableName(field) || 'Unknown'}</Badge></td>
                    <td><code>{getColumnName(field) || 'Unknown'}</code></td>
                    <td className="admin-editor__content-cell">
                      {isEditing ? (
                        <>
                          <Form.Label htmlFor={`field-description-${fieldId}`} className="visually-hidden">Description</Form.Label>
                          <Form.Control
                            id={`field-description-${fieldId}`}
                            as="textarea"
                            rows={4}
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            autoFocus
                          />
                          <span className="admin-editor__character-count">{description.length} characters</span>
                        </>
                      ) : (
                        <span className={getDescription(field).trim() ? '' : 'admin-editor__empty-value'}>
                          {getDescription(field).trim() || 'No description provided'}
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="admin-editor__row-actions">
                        {isEditing ? (
                          <>
                            <Button size="sm" onClick={handleSave} disabled={Boolean(pendingAction) || !description.trim()}>
                              {pendingAction === `edit:${fieldId}` && <Spinner size="sm" className="me-2" />}Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => { setEditingField(null); setDescription(''); }}
                              disabled={Boolean(pendingAction)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline-primary" onClick={() => handleEdit(field)} disabled={Boolean(pendingAction)}>
                              <i className="bi bi-pencil me-1" />Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => setDeleteTarget(field)}
                              disabled={Boolean(pendingAction)}
                              aria-label={`Delete description for ${getColumnName(field)}`}
                            >
                              <i className="bi bi-trash" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <div className="admin-editor__empty">
            <i className="bi bi-search" />
            <h3>No matching field descriptions</h3>
            <p>Try a different search or table filter.</p>
            <Button variant="outline-primary" onClick={() => { setSearchTerm(''); setTableFilter('all'); }}>Reset filters</Button>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={closeCreateModal} size="lg" centered backdrop="static">
        <Form onSubmit={handleAddNew}>
          <Modal.Header closeButton={!pendingAction}>
            <div><p className="admin-editor__eyebrow mb-1">Create</p><Modal.Title>New field description</Modal.Title></div>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">Choose an API table and column, then write the guidance users should see.</p>
            {modalError && <Alert variant="danger">{modalError}</Alert>}
            <Form.Group controlId="field-description-table" className="mb-3">
              <Form.Label>Table</Form.Label>
              <Form.Select value={newField.tableName} onChange={(event) => handleTableChange(event.target.value)} disabled={Boolean(pendingAction)}>
                <option value="">Select a table</option>
                {availableApiTables.map((key) => <option key={key} value={key}>{humanizeKey(key)}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="field-description-column" className="mb-3">
              <Form.Label>Column</Form.Label>
              <div className="admin-editor__select-loading">
                <Form.Select
                  value={newField.columnName}
                  onChange={(event) => setNewField((current) => ({ ...current, columnName: event.target.value }))}
                  disabled={!columnOptions.length || columnLoading || Boolean(pendingAction)}
                >
                  <option value="">{columnLoading ? 'Loading columns...' : 'Select a column'}</option>
                  {columnOptions.map((column) => <option key={column} value={column}>{column}</option>)}
                </Form.Select>
                {columnLoading && <Spinner size="sm" />}
              </div>
            </Form.Group>
            <Form.Group controlId="field-description-content">
              <div className="d-flex justify-content-between gap-3">
                <Form.Label>Description</Form.Label>
                <span className="text-muted small">{newField.description.length} characters</span>
              </div>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Explain what this field means or how it should be completed."
                value={newField.description}
                onChange={(event) => setNewField((current) => ({ ...current, description: event.target.value }))}
                disabled={Boolean(pendingAction)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeCreateModal} disabled={Boolean(pendingAction)}>Cancel</Button>
            <Button
              type="submit"
              disabled={Boolean(pendingAction) || !newField.tableName || !newField.columnName || !newField.description.trim()}
            >
              {pendingAction === 'create' && <Spinner size="sm" className="me-2" />}Create description
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => !pendingAction && setDeleteTarget(null)} centered backdrop="static">
        <Modal.Header closeButton={!pendingAction}><Modal.Title>Delete field description?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="mb-2">The description for <strong>{getColumnName(deleteTarget)}</strong> will be removed.</p>
          <p className="text-muted mb-0">The underlying field and its data will not be affected.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={Boolean(pendingAction)}>Keep description</Button>
          <Button variant="danger" onClick={handleDelete} disabled={Boolean(pendingAction)}>
            {pendingAction.startsWith('delete:') && <Spinner size="sm" className="me-2" />}Delete description
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default AdminFieldDesc;
